import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Stack,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Card,
    CardContent,
    IconButton,
    TextField,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Slider,
    Checkbox,
    Autocomplete,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Toolbar,
    AppBar,
    Fade,
    Backdrop
} from '@mui/material';
import {
    Print as PrintIcon,
    Person as PersonIcon,
    Tune as TuneIcon,
    ExpandMore as ExpandMoreIcon,
    Preview as PreviewIcon,
    Close as CloseIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const FirstPagePrintSettings = () => {
    const { user } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingName, setSettingName] = useState('default');
    const [savedSettings, setSavedSettings] = useState([]);
    const [offlineMode, setOfflineMode] = useState(false);
    const [showRealTimePreview, setShowRealTimePreview] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [fontSize, setFontSize] = useState(14); // Default font size in pixels
    
    // All available customer fields from the database
    const customerFields = [
        { key: 'consumerNumber', label: 'Consumer Number', field: 'consumerNumber' },
        { key: 'consumer_Name', label: 'Consumer Name', field: 'consumer_Name' },
        { key: 'address1', label: 'Address Line 1', field: 'address1' },
        { key: 'address2', label: 'Address Line 2', field: 'address2' },
        { key: 'cardSerialNumber', label: 'Card Serial Number', field: 'cardSerialNumber' },
        { key: 'sV_Number', label: 'SV Number', field: 'sV_Number' },
        { key: 'sV_Date', label: 'SV Date', field: 'sV_Date' },
        { key: 'sV_Number_DBC', label: 'SV Number DBC', field: 'sV_Number_DBC' },
        { key: 'sV_Date_DBC', label: 'SV Date DBC', field: 'sV_Date_DBC' },
        { key: 'no_Of_Cylinders', label: 'Number of Cylinders', field: 'no_Of_Cylinders' },
        { key: 'deposit_Rs', label: 'Deposit (Rs)', field: 'deposit_Rs' },
        { key: 'no_of_Regulrators', label: 'Number of Regulators', field: 'no_of_Regulrators' },
        { key: 'no_of_Regulrators_Deposit', label: 'Regulator Deposit', field: 'no_of_Regulrators_Deposit' },
        { key: 'distributors_Code', label: 'Distributor Code', field: 'distributors_Code' },
        { key: 'distributors_Name', label: 'Distributor Name', field: 'distributors_Name' },
        { key: 'distributors_Address1', label: 'Distributor Address 1', field: 'distributors_Address1' },
        { key: 'distributors_Address2', label: 'Distributor Address 2', field: 'distributors_Address2' },
        { key: 'distributors_Phone', label: 'Distributor Phone', field: 'distributors_Phone' },
        { key: 'distributors_State', label: 'Distributor State', field: 'distributors_State' },
        { key: 'distributors_City', label: 'Distributor City', field: 'distributors_City' },
        { key: 'distributors_PIN', label: 'Distributor PIN', field: 'distributors_PIN' },
        { key: 'issuing_Company', label: 'Issuing Company', field: 'issuing_Company' }
    ];
    
    // Position, visibility, and ordering settings for each field
    const [fieldSettings, setFieldSettings] = useState(() => {
        const defaultSettings = {};
        customerFields.forEach((field, index) => {
            defaultSettings[field.key] = {
                top: 120 + (index * 40),
                left: 200,
                visible: ['consumerNumber', 'consumer_Name', 'address1', 'distributors_Name'].includes(field.key),
                order: index + 1
            };
        });
        return defaultSettings;
    });

    useEffect(() => {
        loadSavedSettings();
        
        // Load the last used template if available
        const lastTemplate = localStorage.getItem('lastPassbookTemplate');
        if (lastTemplate) {
            // Small delay to ensure saved settings are loaded first
            setTimeout(() => {
                loadSettings(lastTemplate);
            }, 100);
        }
    }, []);

    const searchCustomers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setCustomers([]);
            return;
        }

        try {
            setCustomerSearchLoading(true);
            // Add search parameter to the API call
            const response = await api.get(`/api/customers?search=${encodeURIComponent(searchTerm)}`);
            console.log('Search response:', response.data);
            setCustomers(response.data);
        } catch (error) {
            console.error('Error searching customers:', error);
            setError('Failed to search customers');
            setCustomers([]);
        } finally {
            setCustomerSearchLoading(false);
        }
    };

    // Debounced search function
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (customerSearchText) {
                searchCustomers(customerSearchText);
            } else {
                setCustomers([]);
            }
        }, 300); // 300ms delay

        return () => clearTimeout(debounceTimer);
    }, [customerSearchText]);

    const loadSavedSettings = async () => {
        try {
            // Try the real backend API first
            const response = await api.get('/api/passbooksettings/names');
            setSavedSettings(response.data);
        } catch (error) {
            console.log('Backend API not available, using mock template list');
            // Fallback to mock data if API isn't ready yet
            if (error.response?.status === 405 || error.response?.status === 404) {
                setSavedSettings(['default', 'template1', 'template2']);
                setOfflineMode(true);
            } else {
                console.error('Unexpected error loading saved settings:', error);
                setSavedSettings(['default']); // Fallback to at least one template
            }
        }
    };

    const loadSettings = async (settingName) => {
        try {
            setLoading(true);
            // Try the real backend API first
            const response = await api.get(`/api/passbooksettings/${settingName}`);
            const settings = response.data;
            
            const newFieldSettings = { ...fieldSettings };
            let loadedFontSize = 14; // Default font size
            
            settings.forEach(setting => {
                if (newFieldSettings[setting.fieldName]) {
                    newFieldSettings[setting.fieldName] = {
                        top: setting.topPosition,
                        left: setting.leftPosition,
                        visible: setting.isVisible,
                        order: setting.displayOrder || 0
                    };
                    // Get font size from the first setting (since it's common for all fields)
                    if (setting.fontSize) {
                        loadedFontSize = setting.fontSize;
                    }
                }
            });
            setFieldSettings(newFieldSettings);
            setFontSize(loadedFontSize);
            setSettingName(settingName);
            // Save as last used template
            localStorage.setItem('lastPassbookTemplate', settingName);
        } catch (error) {
            console.log('Backend API not available, using current settings');
            // Fallback behavior if API isn't ready yet
            if (error.response?.status === 405 || error.response?.status === 404) {
                setSettingName(settingName);
                // Save as last used template even in fallback mode
                localStorage.setItem('lastPassbookTemplate', settingName);
                // Show a subtle info message instead of error
                setSuccess(`Loaded template "${settingName}" (offline mode)`);
            } else {
                console.error('Unexpected error loading settings:', error);
                setError('Failed to load settings: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteSettings = async (templateName) => {
        if (!window.confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
            return;
        }

        try {
            setLoading(true);
            // Try the real backend API first
            await api.delete(`/api/passbooksettings/${templateName}`);
            
            // Remove from saved settings list
            setSavedSettings(prev => prev.filter(name => name !== templateName));
            
            // If we deleted the currently selected template, reset to default
            if (templateName === settingName) {
                setSettingName('');
                localStorage.removeItem('lastPassbookTemplate');
            }
            
            setSuccess(`Template "${templateName}" deleted successfully`);
        } catch (error) {
            console.log('Backend API not available, simulating delete');
            // Fallback behavior if API isn't ready yet
            if (error.response?.status === 405 || error.response?.status === 404) {
                // Remove from saved settings list (mock behavior)
                setSavedSettings(prev => prev.filter(name => name !== templateName));
                if (templateName === settingName) {
                    setSettingName('');
                    localStorage.removeItem('lastPassbookTemplate');
                }
                setSuccess(`Template "${templateName}" deleted (offline mode)`);
            } else {
                console.error('Unexpected error deleting settings:', error);
                setError('Failed to delete settings: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        try {
            setLoading(true);
            const fieldsToSave = customerFields.map(field => ({
                fieldName: field.key,
                topPosition: fieldSettings[field.key].top,
                leftPosition: fieldSettings[field.key].left,
                isVisible: fieldSettings[field.key].visible,
                displayOrder: fieldSettings[field.key].order || 0,
                fontSize: fontSize
            }));

            console.log('Attempting to save settings:', {
                settingName: settingName,
                fields: fieldsToSave
            });

            // Try the real backend API
            await api.post('/api/passbooksettings/bulk', {
                settingName: settingName,
                fields: fieldsToSave
            });

            // If successful, reload saved settings list
            await loadSavedSettings();
            setError('');
            setSuccess('Settings saved successfully!');
        } catch (error) {
            console.log('Backend API not available, using mock save');
            
            // Fallback to mock behavior if API isn't ready yet
            if (error.response?.status === 405 || error.response?.status === 404) {
                if (!savedSettings.includes(settingName)) {
                    setSavedSettings([...savedSettings, settingName]);
                }
                setError('');
                setSuccess(`Settings saved as "${settingName}" (offline mode)`);
            } else {
                console.error('Unexpected error saving settings:', error);
                setError('Failed to save settings: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    };



    const handlePositionChange = (fieldKey, dimension, value) => {
        setFieldSettings(prev => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                [dimension]: value
            }
        }));
    };

    const handleVisibilityChange = (fieldKey, visible) => {
        setFieldSettings(prev => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                visible: visible
            }
        }));
    };

    const handleOrderChange = (fieldKey, order) => {
        setFieldSettings(prev => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                order: order
            }
        }));
    };

    const formatFieldValue = (customer, fieldKey) => {
        if (!customer) return 'N/A';
        
        switch (fieldKey) {
            case 'sV_Date':
            case 'sV_Date_DBC':
                return customer[fieldKey] ? new Date(customer[fieldKey]).toLocaleDateString() : 'N/A';
            case 'createdDate':
                return new Date(customer.createdDate).toLocaleDateString();
            case 'isActive':
                return customer.isActive ? 'Active' : 'Inactive';
            case 'id':
                return customer.id.toString();
            case 'tenantId':
                return customer.tenantId.toString();
            case 'no_Of_Cylinders':
            case 'no_of_Regulrators':
                return customer[fieldKey]?.toString() || 'N/A';
            case 'deposit_Rs':
            case 'no_of_Regulrators_Deposit':
                return customer[fieldKey] ? `Rs. ${customer[fieldKey]}` : 'N/A';
            default:
                return customer[fieldKey] || 'N/A';
        }
    };

    const printFirstPage = () => {
        if (!selectedCustomer) return;

        // Open print window with dimensions that accommodate 14×9 inch page
        const printWindow = window.open('', '_blank', 'width=900,height=1400');
        const printContent = generateFirstPageHTML();
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for content to load before printing
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    const previewFirstPage = () => {
        if (!selectedCustomer) return;
        setPreviewModalOpen(true);
    };

    const generateFirstPageHTML = (isPreview = false) => {
        const visibleFields = customerFields
            .filter(field => fieldSettings[field.key]?.visible)
            .sort((a, b) => (fieldSettings[a.key]?.order || 0) - (fieldSettings[b.key]?.order || 0));
        
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Gas Passbook - First Page</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 20px;
                        font-size: 14px;
                        position: relative;
                        width: 9in;
                        height: 14in;
                        min-height: 14in;
                        ${isPreview ? 'background-color: #f0f0f0; border: 2px dashed #ccc;' : ''}
                    }
                    .field {
                        position: absolute;
                        font-weight: bold;
                        font-size: ${fontSize}px;
                        ${isPreview ? 'border: 1px solid #007bff; background-color: rgba(0,123,255,0.1); padding: 2px;' : ''}
                    }
                    .field-label {
                        font-size: 10px;
                        color: #666;
                        position: absolute;
                        top: -15px;
                        left: 0;
                        ${isPreview ? 'display: block;' : 'display: none;'}
                    }
                    @page {
                        size: 9in 14in;
                        margin: 0;
                    }
                    @media print {
                        html, body { 
                            width: 9in !important;
                            height: 14in !important;
                            margin: 0 !important; 
                            padding: 0 !important;
                            background: none !important;
                            border: none !important;
                            overflow: hidden !important;
                        }
                        .field { 
                            border: none !important; 
                            background: none !important;
                            padding: 0 !important;
                        }
                        .field-label { display: none !important; }
                        * {
                            -webkit-print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                    }
                </style>
            </head>
            <body>
                ${isPreview ? '<h3 style="text-align: center; color: #666;">PREVIEW MODE - Customer Information Positioning</h3>' : ''}
                
                ${visibleFields.map(field => `
                    <div class="field" style="top: ${fieldSettings[field.key].top + 30}px; left: ${fieldSettings[field.key].left}px;">
                        <div class="field-label">${field.label}</div>
                        ${formatFieldValue(selectedCustomer, field.key)}
                    </div>
                `).join('')}
            </body>
            </html>
        `;
    };

    const resetPositions = () => {
        const defaultSettings = {};
        customerFields.forEach((field, index) => {
            defaultSettings[field.key] = {
                top: 120 + (index * 40),
                left: 200,
                visible: ['consumerNumber', 'consumer_Name', 'address1', 'distributors_Name'].includes(field.key),
                order: index + 1
            };
        });
        setFieldSettings(defaultSettings);
        setFontSize(14); // Reset to default font size
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Passbook First Page Printing
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Print customer information on the first page of a pre-printed passbook
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
                
                {offlineMode && (
                    <Alert severity="info" sx={{ mb: 2, backgroundColor: '#e3f2fd' }}>
                        💾 Offline Mode: Settings are saved locally. They will sync automatically when the server is ready.
                    </Alert>
                )}
            </Box>

            <Stack spacing={3}>
                {/* Settings Management */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Settings Management
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <TextField
                                fullWidth
                                label="Setting Name"
                                value={settingName}
                                onChange={(e) => setSettingName(e.target.value)}
                                placeholder="Enter setting name"
                            />
                        </Box>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel>Load Saved Setting</InputLabel>
                                <Select
                                    value=""
                                    label="Load Saved Setting"
                                    onChange={(e) => loadSettings(e.target.value)}
                                >
                                    {savedSettings.map((name) => (
                                        <MenuItem key={name} value={name}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ minWidth: 160 }}>
                            <Button
                                variant="contained"
                                onClick={saveSettings}
                                disabled={!settingName.trim() || loading}
                                fullWidth
                            >
                                Save Settings
                            </Button>
                        </Box>
                    </Stack>
                        
                        {/* Saved Templates List with Delete Options */}
                        {savedSettings.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Saved Templates:
                                </Typography>
                                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {savedSettings.map((name) => (
                                        <Paper 
                                            key={name} 
                                            sx={{ 
                                                p: 1, 
                                                mb: 1, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'space-between',
                                                backgroundColor: settingName === name ? 'action.selected' : 'background.paper'
                                            }}
                                        >
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    fontWeight: settingName === name ? 'bold' : 'normal',
                                                    color: settingName === name ? 'primary.main' : 'text.primary'
                                                }}
                                            >
                                                {name}
                                                {settingName === name && ' (current)'}
                                            </Typography>
                                            <Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => loadSettings(name)}
                                                    disabled={loading}
                                                    title="Load template"
                                                >
                                                    📁
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => deleteSettings(name)}
                                                    disabled={loading}
                                                    title="Delete template"
                                                    sx={{ color: 'error.main' }}
                                                >
                                                    🗑️
                                                </IconButton>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        )}
                </Paper>

                {/* Customer Selection - Full Width */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Select Customer
                    </Typography>
                    
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 300 }}>
                            <Autocomplete
                                options={customers}
                                getOptionLabel={(option) => `${option.consumerNumber || option.ConsumerNumber} - ${option.consumer_Name || option.Consumer_Name}`}
                                value={selectedCustomer}
                                onChange={(event, newValue) => {
                                    setSelectedCustomer(newValue);
                                }}
                                inputValue={customerSearchText}
                                onInputChange={(event, newInputValue) => {
                                    setCustomerSearchText(newInputValue);
                                }}
                                loading={customerSearchLoading}
                                loadingText="Searching customers..."
                                noOptionsText={customerSearchText.length < 2 ? "Type at least 2 characters to search" : "No customers found"}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Customer"
                                        placeholder="Type consumer name, number, or card serial..."
                                        variant="outlined"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body1">
                                                {option.consumer_Name || option.Consumer_Name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Consumer: {option.consumerNumber || option.ConsumerNumber} | 
                                                Card: {option.cardSerialNumber || option.CardSerialNumber || 'N/A'} |
                                                SV: {option.sV_Number || option.sv_Number || option.SV_Number || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                filterOptions={(x) => x} // Disable client-side filtering since we're doing server-side search
                            />
                        </Box>

                        {selectedCustomer && (
                            <Box sx={{ flex: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            Selected Customer Details
                                        </Typography>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                            <Typography variant="body2">
                                                <strong>Consumer Number:</strong> {selectedCustomer.consumerNumber}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Name:</strong> {selectedCustomer.consumer_Name}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Address:</strong> {selectedCustomer.address1}
                                                {selectedCustomer.address2 && `, ${selectedCustomer.address2}`}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}
                    </Stack>
                </Paper>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ height: { md: '80vh' } }}>
                    {/* Field Settings - Left Half */}

                    {/* Field Settings - Left Half */}
                    <Paper sx={{ p: 3, flex: 1, height: '100%', overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                <TuneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Field Settings
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Configure position and visibility for each customer field
                        </Typography>

                        {customerFields
                            .sort((a, b) => (fieldSettings[a.key]?.order || 0) - (fieldSettings[b.key]?.order || 0))
                            .map((field) => (
                            <Accordion key={field.key} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={fieldSettings[field.key]?.order || 1}
                                                onChange={(e) => handleOrderChange(field.key, parseInt(e.target.value) || 1)}
                                                inputProps={{ min: 1, max: customerFields.length }}
                                                disabled={!fieldSettings[field.key]?.visible}
                                                sx={{ width: 80 }}
                                                onClick={(e) => e.stopPropagation()}
                                                label="Order"
                                            />
                                            <Typography>{field.label}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="caption" sx={{ mr: 2 }}>
                                                Visible: {fieldSettings[field.key]?.visible ? 'Yes' : 'No'}
                                            </Typography>
                                            <Checkbox
                                                checked={fieldSettings[field.key]?.visible || false}
                                                onChange={(e) => handleVisibilityChange(field.key, e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Sample Value: {formatFieldValue(selectedCustomer, field.key)}
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography gutterBottom>Top Position (px)</Typography>
                                                <Slider
                                                    value={fieldSettings[field.key]?.top || 0}
                                                    onChange={(e, value) => handlePositionChange(field.key, 'top', value)}
                                                    min={0}
                                                    max={800}
                                                    valueLabelDisplay="on"
                                                    disabled={!fieldSettings[field.key]?.visible}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography gutterBottom>Left Position (px)</Typography>
                                                <Slider
                                                    value={fieldSettings[field.key]?.left || 0}
                                                    onChange={(e, value) => handlePositionChange(field.key, 'left', value)}
                                                    min={0}
                                                    max={800}
                                                    valueLabelDisplay="on"
                                                    disabled={!fieldSettings[field.key]?.visible}
                                                />
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Paper>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Font Size and Action Buttons */}
                        <Paper sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                {/* Font Size Control */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                                    <Typography variant="body2" sx={{ minWidth: 80 }}>
                                        Font Size:
                                    </Typography>
                                    <Box sx={{ width: 200 }}>
                                        <Slider
                                            value={fontSize}
                                            onChange={(e, value) => setFontSize(value)}
                                            min={8}
                                            max={24}
                                            step={1}
                                            valueLabelDisplay="on"
                                            marks={[
                                                { value: 8, label: '8px' },
                                                { value: 12, label: '12px' },
                                                { value: 14, label: '14px' },
                                                { value: 16, label: '16px' },
                                                { value: 20, label: '20px' },
                                                { value: 24, label: '24px' }
                                            ]}
                                            sx={{
                                                '& .MuiSlider-markLabel': {
                                                    fontSize: '8px'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ minWidth: 40,fontSize:9 }}>
                                        {fontSize}px
                                    </Typography>
                                </Box>
                                
                                {/* Action Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PreviewIcon />}
                                        onClick={previewFirstPage}
                                        disabled={!selectedCustomer}
                                        size="large"
                                    >
                                        Preview
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<PrintIcon />}
                                        onClick={printFirstPage}
                                        disabled={!selectedCustomer}
                                        size="large"
                                        color="success"
                                    >
                                        Print First Page
                                    </Button>
                                    <Button
                                        variant="text"
                                        onClick={resetPositions}
                                        startIcon={<SettingsIcon />}
                                    >
                                        Reset Positions
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Real-time Preview */}
                        <Paper sx={{ 
                            flex: 1, 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}>
                            <Typography variant="h6" sx={{ textAlign: 'center', color: '#666', mb: 1 }}>
                                <PreviewIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Real-time Preview
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#666', mb: 1 }}>
                                Canvas Size: 9" Width × 14" Height (Print Area)
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#666' }}>
                                {selectedCustomer ? 'Adjust sliders to see real-time changes' : 'Select a customer to see preview'}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ 
                            flex: 1,
                            p: 2,
                            backgroundColor: '#f8f9fa',
                            overflow: 'auto',
                            minHeight: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start'
                        }}>
                            {selectedCustomer ? (
                                <Box sx={{ 
                                    position: 'relative', 
                                    width: '360px', // 9 inches * 40px/inch = 360px
                                    height: '560px', // 14 inches * 40px/inch = 560px
                                    backgroundColor: 'white', 
                                    border: '2px dashed #007bff',
                                    borderRadius: '4px',
                                    transform: 'scale(0.8)', // Scale down to fit better in sidebar
                                    transformOrigin: 'top center'
                                }}>
                                    {customerFields
                                        .filter(field => fieldSettings[field.key]?.visible)
                                        .sort((a, b) => (fieldSettings[a.key]?.order || 0) - (fieldSettings[b.key]?.order || 0))
                                        .map((field) => (
                                            <Box
                                                key={field.key}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${fieldSettings[field.key].top + 30}px`,
                                                    left: `${fieldSettings[field.key].left}px`,
                                                    padding: '2px 4px',
                                                    fontSize: `${fontSize}px`,
                                                    fontWeight: 'bold',
                                                    color: '#000',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,123,255,0.1)',
                                                        transform: 'scale(1.05)'
                                                    }
                                                }}
                                                title={`${field.label} - Order: ${fieldSettings[field.key]?.order || 1}`}
                                            >
                                                {formatFieldValue(selectedCustomer, field.key)}
                                            </Box>
                                        ))
                                    }
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    height: '100%',
                                    backgroundColor: 'white',
                                    border: '2px dashed #ddd',
                                    borderRadius: '4px',
                                    color: '#666'
                                }}>
                                    <Typography variant="h6">
                                        Please select a customer to see preview
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                    </Box>
                </Stack>
            </Stack>

            {/* Preview Modal */}
            <Dialog
                fullScreen
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                TransitionComponent={Fade}
                TransitionProps={{
                    timeout: 300,
                }}
            >
                <AppBar sx={{ position: 'relative', bgcolor: 'primary.main' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                           Passbook Preview - {selectedCustomer?.consumer_Name || 'Customer'}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={() => {
                                printFirstPage();
                                setPreviewModalOpen(false);
                            }}
                            sx={{ mr: 2, color: 'white', borderColor: 'white' }}
                        >
                            Print Now
                        </Button>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setPreviewModalOpen(false)}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogContent sx={{ 
                    p: 0, // Remove default padding
                    pt: 1, // Significantly increased top padding to clear AppBar
                    pb: 8, // Increased bottom padding
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    minHeight: 'calc(100vh - 64px)', // Account for AppBar height
                    overflow: 'auto' // Allow scrolling if needed
                }}>
                    {selectedCustomer && (
                        <Paper
                            elevation={8}
                            sx={{
                                width: '648px', // 9 inches * 72px/inch = 648px
                                height: '1008px', // 14 inches * 72px/inch = 1008px
                                position: 'relative',
                                backgroundColor: 'white',
                                transform: 'scale(0.7)', // Scale for better fit in modal
                                transformOrigin: 'top center',
                                border: '1px solid #ddd',
                                mt: 2, // Significantly increased top margin to clear AppBar
                                mb: 8 // Increased bottom margin
                            }}
                        >
                            <Box sx={{ 
                                p: 2,
                                height: '100%',
                                position: 'relative'
                            }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        textAlign: 'center', 
                                        mb: 3, 
                                        color: '#666',
                                        borderBottom: '2px solid #eee',
                                        pb: 1
                                    }}
                                >
                                    Gas Passbook - First Page -
                                </Typography>
                                
                                {customerFields
                                    .filter(field => fieldSettings[field.key]?.visible)
                                    .sort((a, b) => (fieldSettings[a.key]?.order || 0) - (fieldSettings[b.key]?.order || 0))
                                    .map((field) => (
                                        <Box
                                            key={field.key}
                                            sx={{
                                                position: 'absolute',
                                                top: `calc(${(fieldSettings[field.key].top / 800) * 100}% + 80px)`,
                                                left: `${(fieldSettings[field.key].left / 800) * 100}%`,
                                                padding: '2px 4px',
                                                minWidth: '100px'
                                            }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: `${fontSize}px`,
                                                    color: '#000'
                                                }}
                                            >
                                                {formatFieldValue(selectedCustomer, field.key)}
                                            </Typography>
                                        </Box>
                                    ))
                                }
                            </Box>
                        </Paper>
                    )}
                </DialogContent>
            </Dialog>


        </Container>
    );
};

export default FirstPagePrintSettings;